/**
 * PatientsList Sub-component for ContextLinkingButton
 * Displays recent patients in a dropdown menu for linking to Evidence queries.
 * MobilePatientsList variant uses button elements for bottom sheet compatibility.
 */

import { type LinkedPatientContext } from '@/atoms/chatAtoms';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { Check, UserRound } from 'lucide-react';
import { useMemo } from 'react';

interface PatientsListProps {
  linkedPatient: LinkedPatientContext | null;
  onSelect: (patient: LinkedPatientContext) => void;
  onClear: () => void;
  /** When true, disables selection and clear (context is locked after chat starts) */
  disabled?: boolean;
}

/** Shared patient item data for render props */
interface PatientItemData {
  patientId: string;
  displayName: string;
  isLinked: boolean;
  demographicString?: string;
  onItemClick: (() => void) | undefined;
}

/** Hook that encapsulates shared patient list data fetching logic */
function usePatientsListData(
  linkedPatient: LinkedPatientContext | null,
  onSelect: (patient: LinkedPatientContext) => void,
  onClear: () => void,
  disabled: boolean
) {
  const { t } = useTranslations();
  const { data: patients, isLoading } = useQueryPatientProfiles();

  const recentPatients = useMemo(() => {
    if (!patients) return [];
    return patients.filter((p) => p.id);
  }, [patients]);

  const patientItems: PatientItemData[] = useMemo(() => {
    return recentPatients.map((patient) => {
      const isLinked = linkedPatient?.id === patient.id;
      const fullName = [patient.first_name, patient.last_name].filter(Boolean).join(' ');
      const displayName =
        fullName ||
        patient.demographic_string ||
        t('chat.contextLinking.unknownPatient' as TranslationKey);
      // Only show demographic string as subtitle when it's not already the display name
      const showDemographicString = fullName && patient.demographic_string;

      return {
        patientId: patient.id!,
        displayName,
        isLinked,
        demographicString: showDemographicString
          ? patient.demographic_string
          : undefined,
        onItemClick: disabled
          ? undefined
          : isLinked
            ? onClear
            : () =>
                onSelect({
                  id: patient.id!,
                  name: displayName,
                  demographicString: showDemographicString
                    ? patient.demographic_string
                    : undefined,
                }),
      };
    });
  }, [recentPatients, linkedPatient?.id, disabled, onClear, onSelect, t]);

  return {
    patientItems,
    isLoading,
  };
}

/** Shared empty state component */
const PatientsEmptyState = ({ className }: { className?: string }) => {
  const { t } = useTranslations();
  return (
    <div className={cn('px-3 text-center', className)}>
      <TypographyP2 className="text-text-secondary">
        {t('chat.contextLinking.noPatientsFound' as TranslationKey)}
      </TypographyP2>
    </div>
  );
};

/** Shared loading state component */
const PatientsLoadingState = ({
  loaderSize,
  className,
}: {
  loaderSize: number;
  className?: string;
}) => (
  <div className={cn('flex items-center justify-center', className)}>
    <Loader size={loaderSize} />
  </div>
);

/** Props for patient item content - shared between desktop and mobile */
interface PatientItemContentProps {
  displayName: string;
  isLinked: boolean;
  demographicString?: string;
  iconSize: number;
  checkSize: number;
  detailsPaddingStart: string;
}

/** Shared patient item content */
const PatientItemContent = ({
  displayName,
  isLinked,
  demographicString,
  iconSize,
  checkSize,
  detailsPaddingStart,
}: PatientItemContentProps) => (
  <>
    <div className="flex w-full items-center gap-2">
      <UserRound size={iconSize} className="shrink-0 text-text-secondary" />
      <TypographyP2 className="flex-1 truncate">{displayName}</TypographyP2>
      {isLinked && <Check size={checkSize} className="shrink-0 text-text-primary" />}
    </div>
    {demographicString && (
      <TypographyCaption className={cn('text-text-tertiary', detailsPaddingStart)}>
        {demographicString}
      </TypographyCaption>
    )}
  </>
);

export const PatientsList = ({
  linkedPatient,
  onSelect,
  onClear,
  disabled = false,
}: PatientsListProps) => {
  const { patientItems, isLoading } = usePatientsListData(
    linkedPatient,
    onSelect,
    onClear,
    disabled
  );

  if (isLoading) {
    return <PatientsLoadingState loaderSize={20} className="py-6" />;
  }

  if (patientItems.length === 0) {
    return <PatientsEmptyState className="py-6" />;
  }

  return (
    <div>
      {patientItems.map((item) => (
        <DropdownMenuItem
          key={item.patientId}
          onClick={item.onItemClick}
          disabled={disabled}
          className={cn(
            'flex flex-col items-start gap-0.5 py-2',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <PatientItemContent
            displayName={item.displayName}
            isLinked={item.isLinked}
            demographicString={item.demographicString}
            iconSize={14}
            checkSize={14}
            detailsPaddingStart="ps-5"
          />
        </DropdownMenuItem>
      ))}
    </div>
  );
};

/**
 * Mobile-optimized patients list for bottom sheet
 * Uses button elements instead of DropdownMenuItem for drawer compatibility
 */
export const MobilePatientsList = ({
  linkedPatient,
  onSelect,
  onClear,
  disabled = false,
}: PatientsListProps) => {
  const { patientItems, isLoading } = usePatientsListData(
    linkedPatient,
    onSelect,
    onClear,
    disabled
  );

  if (isLoading) {
    return <PatientsLoadingState loaderSize={24} className="py-8" />;
  }

  if (patientItems.length === 0) {
    return <PatientsEmptyState className="py-8" />;
  }

  return (
    <div className="flex flex-col">
      {patientItems.map((item) => (
        <button
          key={item.patientId}
          type="button"
          onClick={item.onItemClick}
          disabled={disabled}
          className={cn(
            'flex w-full flex-col items-start gap-1 rounded-lg p-3 text-start',
            'hover:bg-surface-hover active:bg-surface-pressed',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <PatientItemContent
            displayName={item.displayName}
            isLinked={item.isLinked}
            demographicString={item.demographicString}
            iconSize={16}
            checkSize={16}
            detailsPaddingStart="ps-6"
          />
        </button>
      ))}
    </div>
  );
};
